import type { PanderrTemplateDefinition } from "../types";

export const AUTHOR_PORTFOLIO_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "author-portfolio-modern",
  name: "Author Portfolio Modern",
  description:
    "A polished personal-brand website for authors, speakers, consultants, creatives, and individual professionals.",
  category: "portfolio",

  navigation: [
    { label: "About", href: "/about/" },
    { label: "Portfolio", href: "/work/" },
    { label: "Speaking", href: "/speaking/" },
    { label: "Press", href: "/press/" },
    { label: "Contact", href: "/contact/" },
  ],

  headerCta: {
    label: "Work With Me",
    href: "/contact/",
    variant: "primary",
  },

  footer: {
    description:
      "Ideas, work, and expertise brought together in one clear personal brand.",
    columns: [
      {
        heading: "Explore",
        links: [
          { label: "About", href: "/about/" },
          { label: "Portfolio", href: "/work/" },
          { label: "Speaking", href: "/speaking/" },
        ],
      },
      {
        heading: "Connect",
        links: [
          { label: "Press", href: "/press/" },
          { label: "Contact", href: "/contact/" },
          { label: "Work With Me", href: "/contact/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Ideas. Experience. Perspective.",
      heading: "Turn expertise into work that people remember.",
      body: "A personal brand built around thoughtful ideas, meaningful work, and the experience you bring to the table.",
      images: [
        {
          src: "/studio/assets/stock/images/woman working on laptop and coffee.jpeg",
          alt: "Professional working on a personal brand",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Explore My Work",
          href: "/work/",
          variant: "primary",
        },
        {
          label: "Work With Me",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "What I bring",
      heading:
        "Clear thinking, strong communication, and work grounded in experience.",
      body: "Use this space to introduce the perspective, credibility, and point of view that make your work distinctive.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "Ways to work together",
      heading: "Expertise that can take different forms.",
      body: "Customize these areas around writing, speaking, consulting, creative work, advisory services, or other professional offerings.",
      services: [
        {
          title: "Writing & Ideas",
          description:
            "Books, articles, essays, newsletters, research, or thought leadership built around your expertise.",
          image: {
            src: "/studio/assets/stock/images/jessbaileydesign-notebook-g07aaf6eb3_1920_original.jpg",
            alt: "Notebook and writing workspace",
            role: "service",
          },
          href: "/work/",
        },
        {
          title: "Speaking & Events",
          description:
            "Keynotes, workshops, panels, presentations, and conversations designed to inform and engage.",
          image: {
            src: "/studio/assets/stock/images/six people in a business meeting.jpg",
            alt: "Professional speaking and collaboration",
            role: "service",
          },
          href: "/speaking/",
        },
        {
          title: "Consulting & Advisory",
          description:
            "Strategic guidance, creative direction, or professional support tailored to your audience or organization.",
          image: {
            src: "/studio/assets/stock/images/two people in a meeting holding pens.jpg",
            alt: "Professional consulting meeting",
            role: "service",
          },
          href: "/contact/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "About me",
      heading:
        "The work matters more when people understand the person behind it.",
      body: "Use this section for your background, experience, point of view, and the story behind the work you do.",
      images: [
        {
          src: "/studio/assets/stock/images/austin-distel-wawEfYdpkag-unsplash.jpg",
          alt: "Professional personal brand portrait environment",
          role: "about",
        },
      ],
      buttons: [
        {
          label: "Read My Story",
          href: "/about/",
          variant: "text",
        },
      ],
    },

    {
      id: "projects",
      type: "projects",
      eyebrow: "Featured work",
      heading: "A few things worth exploring.",
      body: "Showcase books, projects, articles, campaigns, creative work, research, or client engagements.",
      projects: [
        {
          title: "Featured Project",
          description:
            "Highlight a standout piece of work, publication, engagement, or creative project.",
          image: {
            src: "/studio/assets/stock/images/laptop on desk with dashboard.jpeg",
            alt: "Featured professional project",
            role: "project",
          },
          href: "/work/",
        },
        {
          title: "Selected Writing",
          description:
            "Feature an article, book, essay, publication, or body of written work.",
          image: {
            src: "/studio/assets/stock/images/jessbaileydesign-notebook-g07aaf6eb3_1920_original.jpg",
            alt: "Writing project",
            role: "project",
          },
          href: "/work/",
        },
        {
          title: "Speaking & Media",
          description:
            "Highlight talks, interviews, media appearances, or other public-facing work.",
          image: {
            src: "/studio/assets/stock/images/six people in a business meeting.jpg",
            alt: "Speaking and media work",
            role: "project",
          },
          href: "/press/",
        },
      ],
    },

    {
      id: "testimonials",
      type: "testimonials",
      eyebrow: "What people say",
      heading: "Work that leaves an impression.",
      testimonials: [
        {
          quote:
            "Thoughtful, clear, and incredibly good at turning complex ideas into something people can use.",
          name: "Client",
          detail: "Consulting engagement",
        },
        {
          quote: "A compelling voice and a memorable perspective.",
          name: "Event Organizer",
          detail: "Speaking engagement",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Let's work together",
      heading: "Have a project, event, or idea in mind?",
      body: "Tell me what you're working on and let's see whether there's a good fit.",
      buttons: [
        {
          label: "Start a Conversation",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "about",
      name: "About",
      title: "About",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About me",
          heading: "Experience, perspective, and the story behind the work.",
          body: "Use this page to introduce your background, expertise, personal story, values, and the work that shaped your point of view.",
          images: [
            {
              src: "/studio/assets/stock/images/austin-distel-wawEfYdpkag-unsplash.jpg",
              alt: "Professional personal brand",
              role: "hero",
            },
          ],
        },
        {
          id: "about-story",
          type: "about",
          eyebrow: "My story",
          heading: "The path that led here.",
          body: "Share the professional journey, experiences, interests, and perspective that define your work today.",
          images: [
            {
              src: "/studio/assets/stock/images/woman working on laptop and coffee.jpeg",
              alt: "Professional working",
              role: "about",
            },
          ],
        },
        {
          id: "about-trust",
          type: "trust",
          eyebrow: "What guides my work",
          heading: "Curiosity, clarity, and useful ideas.",
          body: "Use this section for your values, philosophy, process, or the principles that shape how you work.",
        },
        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Want to connect?",
          heading: "Let's start a conversation.",
          buttons: [
            {
              label: "Contact Me",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "work",
      name: "Portfolio",
      title: "Portfolio",
      slug: "/work/",
      sections: [
        {
          id: "work-hero",
          type: "hero",
          eyebrow: "Selected work",
          heading: "Ideas and projects brought to life.",
          body: "Use this page for books, articles, creative projects, case studies, campaigns, research, products, or professional work.",
          images: [
            {
              src: "/studio/assets/stock/images/laptop on desk with dashboard.jpeg",
              alt: "Professional portfolio work",
              role: "hero",
            },
          ],
        },
        {
          id: "work-projects",
          type: "projects",
          eyebrow: "Portfolio",
          heading: "A selection of work.",
          projects: [
            {
              title: "Featured Project",
              description:
                "Describe a major project, publication, engagement, or piece of work.",
              image: {
                src: "/studio/assets/stock/images/laptop on desk with dashboard.jpeg",
                alt: "Featured portfolio project",
                role: "project",
              },
            },
            {
              title: "Written Work",
              description:
                "Feature a book, article, essay, newsletter, research project, or publication.",
              image: {
                src: "/studio/assets/stock/images/jessbaileydesign-notebook-g07aaf6eb3_1920_original.jpg",
                alt: "Written work",
                role: "project",
              },
            },
            {
              title: "Client or Creative Work",
              description:
                "Highlight consulting, strategy, creative, design, advisory, or collaborative work.",
              image: {
                src: "/studio/assets/stock/images/two people in a meeting holding pens.jpg",
                alt: "Professional client work",
                role: "project",
              },
            },
          ],
        },
        {
          id: "work-cta",
          type: "cta",
          eyebrow: "Have something in mind?",
          heading: "Let's talk about your project.",
          buttons: [
            {
              label: "Work With Me",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "speaking",
      name: "Speaking",
      title: "Speaking",
      slug: "/speaking/",
      sections: [
        {
          id: "speaking-hero",
          type: "hero",
          eyebrow: "Speaking & events",
          heading: "Ideas designed to start better conversations.",
          body: "Use this page for keynotes, workshops, panels, interviews, corporate events, conferences, and public appearances.",
          images: [
            {
              src: "/studio/assets/stock/images/six people in a business meeting.jpg",
              alt: "Professional speaking engagement",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Ask About Speaking",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
        {
          id: "speaking-topics",
          type: "services",
          eyebrow: "Topics",
          heading: "Conversations worth having.",
          services: [
            {
              title: "Signature Topic",
              description:
                "Use this card for your primary keynote, subject area, or signature presentation.",
            },
            {
              title: "Workshops & Teams",
              description:
                "Feature interactive sessions, leadership workshops, team programs, or facilitated conversations.",
            },
            {
              title: "Panels & Media",
              description:
                "Highlight interviews, podcasts, panels, commentary, or other media participation.",
            },
          ],
        },
        {
          id: "speaking-process",
          type: "process",
          eyebrow: "Booking",
          heading: "Simple from inquiry to event day.",
          steps: [
            {
              title: "Tell me about the event",
              description:
                "Share the audience, format, date, goals, and topic you're considering.",
            },
            {
              title: "Shape the session",
              description:
                "We'll align the topic and format with the needs of your audience.",
            },
            {
              title: "Deliver a strong experience",
              description:
                "Create a thoughtful, useful session designed for the room.",
            },
          ],
        },
      ],
    },

    {
      id: "press",
      name: "Press",
      title: "Press & Media",
      slug: "/press/",
      sections: [
        {
          id: "press-hero",
          type: "hero",
          eyebrow: "Press & media",
          heading: "Interviews, features, conversations, and appearances.",
          body: "Use this page to collect press coverage, podcasts, interviews, media mentions, awards, or other public recognition.",
          images: [
            {
              src: "/studio/assets/stock/images/proxyclick-visitor-management-system-l90zRbWvCoE-unsplash.jpg",
              alt: "Professional media and press setting",
              role: "hero",
            },
          ],
        },
        {
          id: "press-projects",
          type: "projects",
          eyebrow: "Featured coverage",
          heading: "Selected media and appearances.",
          projects: [
            {
              title: "Featured Interview",
              description:
                "Add a notable interview, article, podcast, or media appearance.",
            },
            {
              title: "Press Feature",
              description:
                "Highlight a publication, feature story, profile, or media mention.",
            },
            {
              title: "Recognition",
              description:
                "Use this space for awards, honors, rankings, endorsements, or notable recognition.",
            },
          ],
        },
        {
          id: "press-cta",
          type: "cta",
          eyebrow: "Media inquiry",
          heading: "Looking for an interview, quote, or appearance?",
          buttons: [
            {
              label: "Contact Me",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Let's connect",
          heading: "Have a project, event, or idea worth discussing?",
          body: "Use this page for consulting inquiries, speaking requests, collaborations, media opportunities, and general contact.",
          images: [
            {
              src: "/studio/assets/stock/images/woman working on laptop and coffee.jpeg",
              alt: "Professional contact and collaboration",
              role: "hero",
            },
          ],
        },
        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Get in touch",
          heading: "Start a conversation.",
          body: "Update your email, phone, professional links, inquiry details, and preferred contact information in Studio.",
        },
      ],
    },
  ],
};
